import React from 'react';
import type { ReactNode } from 'react';
import useStyles from '../../theme/useStyles';

// The pieces of a module's Summary tab, as the Cemetery summary first drew
// them: a grid of KPI tiles, report tables with an inline bar per row, and a
// bold total line under a table. Shared so every module's summary looks alike.

export interface KpiGridProps {
  children?: ReactNode;
  isDarkMode?: boolean;
}

/** The row of KPI tiles at the top of a summary; wraps on narrow screens. */
export const KpiGrid = ({ children, isDarkMode }: KpiGridProps) => {
  const t = useStyles(isDarkMode).t;
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
      gap: t.spacing.md, marginBottom: t.spacing.lg
    }}>
      {children}
    </div>
  );
};

export interface SummaryColumnsProps {
  children?: ReactNode;
  isDarkMode?: boolean;
}

/** Report panels side by side where there is room, stacked where there is not. */
export const SummaryColumns = ({ children, isDarkMode }: SummaryColumnsProps) => {
  const t = useStyles(isDarkMode).t;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: t.spacing.lg }}>
      {children}
    </div>
  );
};

export interface BarCellProps {
  value: number;
  /** The largest value in the table; the bar is drawn as a share of it. */
  max: number;
  color?: string;
  /** Draw the bar in a track and print the percentage beside it (for a share of 100). */
  showPercent?: boolean;
  isDarkMode?: boolean;
}

/** A horizontal bar for one row of a report table. */
export const BarCell = ({ value, max, color, showPercent, isDarkMode }: BarCellProps) => {
  const t = useStyles(isDarkMode).t;
  const fill = color || t.info;
  const pct = max > 0 ? Math.max(value > 0 ? 2 : 0, Math.min(100, (value / max) * 100)) : 0;

  if (!showPercent) {
    return <div style={{ height: '10px', borderRadius: '4px', backgroundColor: fill, opacity: 0.85, width: `${pct}%` }} />;
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{ flex: 1, height: '10px', borderRadius: '4px', backgroundColor: t.bgSubtle, border: `1px solid ${t.border}` }}>
        <div style={{ width: `${pct}%`, height: '100%', borderRadius: '4px', backgroundColor: fill, opacity: 0.85 }} />
      </div>
      <span style={{ minWidth: '44px', textAlign: 'right' }}>{Math.round(pct)}%</span>
    </div>
  );
};

export interface TotalRowProps {
  label: ReactNode;
  value: ReactNode;
  color?: string;
  isDarkMode?: boolean;
}

/** The bold total under a report table — pass it as TablePanel's `below`. */
export const TotalRow = ({ label, value, color, isDarkMode }: TotalRowProps) => {
  const t = useStyles(isDarkMode).t;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: '12px',
      marginTop: '10px', paddingTop: '10px',
      borderTop: `2px solid ${t.borderStrong}`, fontWeight: 'bold'
    }}>
      <span>{label}</span>
      <span style={{ color: color || t.textMain, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
};

export interface SummaryNoteProps {
  children?: ReactNode;
  isDarkMode?: boolean;
}

/** A quiet line explaining why a figure is blank (e.g. not enough history yet). */
export const SummaryNote = ({ children, isDarkMode }: SummaryNoteProps) => {
  const t = useStyles(isDarkMode).t;
  return <div style={{ color: t.textFaint, fontSize: t.fontSize.sm, marginBottom: t.spacing.md }}>{children}</div>;
};
